import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight,
  CalendarDays,
  Camera,
  Image as ImageIcon,
  RefreshCw,
  X,
} from "lucide-react";
import { searchGarment } from "./api";
import {
  saveMeetingDraft,
  type MeetingDraftInput,
} from "./meeting";

type AnalysisStatus =
  | "idle"
  | "selected"
  | "revealing"
  | "success"
  | "error";
type BackgroundStatus = "idle" | "loading" | "ready" | "error";
type JsonRecord = Record<string, unknown>;

type AttributeView = {
  key: string;
  label: string;
  value: string;
  confidence?: string;
};

type MetricView = {
  label: string;
  value: string;
};

type ResultView = {
  attributes: AttributeView[];
  metrics: MetricView[];
};

type SearchOutcome =
  | { ok: true; response: unknown }
  | { ok: false; error: string; aborted?: boolean };

type MeetingSaveState = "idle" | "saving" | "saved" | "sent" | "error";

const MAX_FILE_BYTES = 12 * 1024 * 1024;
const MAX_IMAGE_EDGE = 1600;
// What the classifier receives. Kept at the dimensions the API used before the
// single-decode change, so the model sees exactly the same image as it did.
const ANALYSIS_EDGE = 768;
const ANALYSIS_QUALITY = 0.85;
// iOS hands back HEIC/HEIF whenever `capture` is used or the user picks an
// unconverted original. The canvas re-encode below normalises every accepted
// type to JPEG, so the classifier never sees HEIC — but the browser must be
// able to decode it first, which Safari can and most desktop browsers cannot.
const SUPPORTED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);
const MEETING_STORAGE_KEY = "cikada-meeting-draft-v1";

function newDraftId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function emptyMeetingDraft(): MeetingDraftInput {
  return {
    draftId: newDraftId(),
    name: "",
    email: "",
    organization: "",
    intent: "customer",
    message: "",
    availability: "Tomorrow",
  };
}

function loadMeetingDraft(): MeetingDraftInput {
  try {
    const stored = localStorage.getItem(MEETING_STORAGE_KEY);
    if (!stored) {
      return emptyMeetingDraft();
    }

    const parsed = JSON.parse(stored) as Partial<MeetingDraftInput>;
    return {
      ...emptyMeetingDraft(),
      ...parsed,
      draftId: parsed.draftId || newDraftId(),
      intent:
        parsed.intent === "investor" || parsed.intent === "talent"
          ? parsed.intent
          : "customer",
    };
  } catch {
    return emptyMeetingDraft();
  }
}

function hasMeetingDetails(draft: MeetingDraftInput): boolean {
  return Boolean(
    draft.name.trim() ||
      draft.email.trim() ||
      draft.organization.trim() ||
      draft.message.trim(),
  );
}

const ATTRIBUTE_FIELDS = [
  { key: "category", label: "Category", aliases: ["category", "garment_type", "type", "article_type"] },
  { key: "color", label: "Color", aliases: ["color", "colour", "primary_color"] },
  { key: "condition", label: "Condition", aliases: ["condition_normalized", "condition"] },
  { key: "material", label: "Material", aliases: ["material", "materials", "fabric"] },
  { key: "pattern", label: "Pattern", aliases: ["pattern", "print"] },
  { key: "season", label: "Season", aliases: ["season", "seasons"] },
  { key: "size", label: "Size", aliases: ["size", "normalized_size"] },
  { key: "market", label: "Market", aliases: ["market", "target_market", "department"] },
] as const;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function firstValue(record: JsonRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== "") {
      return record[key];
    }
  }
  return undefined;
}

function unwrapResponse(raw: unknown): JsonRecord {
  let current: unknown = raw;
  const seen = new Set<unknown>();

  for (let depth = 0; depth < 8 && isRecord(current) && !seen.has(current); depth += 1) {
    seen.add(current);

    if (
      isRecord(current.item) ||
      isRecord(current.attributes) ||
      isRecord(current.appraisal)
    ) {
      return current;
    }

    const next = firstValue(current, [
      "result",
      "data",
      "payload",
      "output",
      "response",
    ]);

    if (!isRecord(next)) {
      break;
    }

    current = next;
  }

  return isRecord(current) ? current : {};
}

function displayLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function numericValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(",", ".").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (isRecord(value)) {
    return numericValue(
      firstValue(value, ["value", "amount", "estimate", "median", "score"]),
    );
  }

  return undefined;
}

function textValue(value: unknown): string {
  if (typeof value === "string") {
    const listMatch = value.match(/^\[(['"])(.*)\1\]$/);
    if (listMatch) {
      return listMatch[2].replace(/['"]\s*,\s*['"]/g, ", ");
    }
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(textValue).filter(Boolean).join(", ");
  }

  if (isRecord(value)) {
    const preferred = firstValue(value, [
      "label",
      "value",
      "prediction",
      "name",
      "class",
      "category",
      "material",
    ]);

    if (preferred !== undefined) {
      return textValue(preferred);
    }
  }

  return "";
}

function confidenceValue(value: unknown): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const confidence = numericValue(
    firstValue(value, ["confidence", "probability", "score"]),
  );

  if (confidence === undefined) {
    return undefined;
  }

  const percentage = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(percentage)}%`;
}

function formatPrice(value: unknown, currencyValue: unknown): string {
  if (typeof value === "string" && /[A-Za-z$€£]/.test(value)) {
    return value;
  }

  const amount = numericValue(value);
  if (amount === undefined) {
    return textValue(value);
  }

  const nestedCurrency = isRecord(value)
    ? firstValue(value, ["currency", "currency_code", "currencyCode"])
    : undefined;
  const currency = textValue(nestedCurrency ?? currencyValue).toUpperCase();

  if (/^[A-Z]{3}$/.test(currency)) {
    return new Intl.NumberFormat("en-SE", {
      style: "currency",
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  return new Intl.NumberFormat("en-SE", {
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatProbability(value: unknown): string {
  const probability = numericValue(value);
  if (probability === undefined) {
    return textValue(value);
  }

  return `${Math.round(probability <= 1 ? probability * 100 : probability)}%`;
}

function formatDays(value: unknown): string {
  const days = numericValue(value);
  if (days === undefined) {
    return textValue(value);
  }

  return `${Math.round(days)} days`;
}

function normalizeResult(raw: unknown): ResultView {
  const root = unwrapResponse(raw);
  const item = isRecord(root.item) ? root.item : root;
  const attributesRecord = isRecord(item.attributes)
    ? item.attributes
    : isRecord(item.predictions)
      ? item.predictions
      : {};
  const appraisal = isRecord(item.appraisal)
    ? item.appraisal
    : isRecord(root.appraisal)
      ? root.appraisal
      : {};

  const attributes = ATTRIBUTE_FIELDS.flatMap((field) => {
    const sourceKey = field.aliases.find(
      (alias) => attributesRecord[alias] !== undefined,
    );
    if (!sourceKey) return [];

    const source = attributesRecord[sourceKey];
    const value = textValue(source);
    return value
      ? [{
          key: field.key,
          label: field.label,
          value,
          confidence: confidenceValue(source),
        }]
      : [];
  });

  const currency = firstValue(appraisal, [
    "currency",
    "currency_code",
    "currencyCode",
  ]) ?? (appraisal.price_eur !== undefined ? "EUR" : undefined);
  const price = firstValue(appraisal, [
    "estimated_price",
    "estimatedPrice",
    "price",
    "resale_price",
    "resalePrice",
    "median_price",
    "medianPrice",
    "valuation",
    "price_eur",
  ]);
  const probability = firstValue(appraisal, [
    "sell_probability",
    "sellProbability",
    "sale_probability",
    "saleProbability",
    "probability_of_sale",
  ]);
  const days = firstValue(appraisal, [
    "expected_days_to_sell",
    "expectedDaysToSell",
    "days_to_sale",
    "daysToSale",
    "days_to_sell",
    "daysToSell",
  ]);

  const metrics: MetricView[] = [
    price !== undefined
      ? { label: "Estimated price", value: formatPrice(price, currency) }
      : undefined,
    probability !== undefined
      ? { label: "Sell probability", value: formatProbability(probability) }
      : undefined,
    days !== undefined
      ? { label: "Expected time to sale", value: formatDays(days) }
      : undefined,
  ].filter((metric): metric is MetricView => Boolean(metric?.value));

  if (attributes.length === 0 && metrics.length === 0) {
    throw new Error("The analysis completed without any readable results.");
  }

  return { attributes, metrics };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      // The previous fallback read the raw file and sent those bytes labelled
      // as JPEG, so an undecodable HEIC reached the classifier as garbage.
      // Failing here is honest: the caller offers a retry.
      reject(
        new Error(
          "This photo could not be opened. Try again, or pick a different one.",
        ),
      );
    };
    image.src = objectUrl;
  });
}

function drawToJpeg(
  image: HTMLImageElement,
  maxEdge: number,
  quality: number,
): string {
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("This browser cannot prepare the selected image.");
  }

  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Decodes the file ONCE and derives both the preview and the bytes the
 * classifier analyses. These used to be two independent decodes of the same
 * file; on iOS the second one could fail on a photo the first had just decoded
 * fine, which surfaced as "One of the selected images could not be read."
 */
async function prepareImage(file: File): Promise<{
  base64: string;
  preview: string;
}> {
  // Browsers occasionally report an empty type for HEIC from the camera, so an
  // unknown type is passed to the decoder rather than rejected outright — an
  // undecodable file still fails, just with a clearer message.
  if (file.type && !SUPPORTED_TYPES.has(file.type)) {
    throw new Error("Choose a JPEG, PNG, or WebP image.");
  }

  if (file.size > MAX_FILE_BYTES) {
    throw new Error("Choose an image smaller than 12 MB.");
  }

  const image = await loadImage(file);
  const preview = drawToJpeg(image, MAX_IMAGE_EDGE, 0.88);
  const base64 = drawToJpeg(image, ANALYSIS_EDGE, ANALYSIS_QUALITY).split(",")[1];

  return { base64, preview };
}

function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const imageRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const backgroundPromiseRef = useRef<Promise<SearchOutcome> | null>(null);
  const [status, setStatus] = useState<AnalysisStatus>("idle");
  const [backgroundStatus, setBackgroundStatus] =
    useState<BackgroundStatus>("idle");
  const [preview, setPreview] = useState<string | null>(null);
  const [rawResult, setRawResult] = useState<unknown>(null);
  const [error, setError] = useState("");
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingDraft, setMeetingDraft] =
    useState<MeetingDraftInput>(loadMeetingDraft);
  const [meetingSaveState, setMeetingSaveState] =
    useState<MeetingSaveState>("idle");
  const [meetingError, setMeetingError] = useState("");
  const lastSavedDraftRef = useRef("");

  const result = useMemo(() => {
    if (status !== "success") {
      return null;
    }

    return normalizeResult(rawResult);
  }, [rawResult, status]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    localStorage.setItem(MEETING_STORAGE_KEY, JSON.stringify(meetingDraft));

    if (!hasMeetingDetails(meetingDraft) || meetingSaveState === "sent") {
      return;
    }

    const serialized = JSON.stringify(meetingDraft);
    if (serialized === lastSavedDraftRef.current) {
      return;
    }

    setMeetingSaveState("saving");
    const timer = window.setTimeout(async () => {
      try {
        await saveMeetingDraft(meetingDraft, false);
        lastSavedDraftRef.current = serialized;
        setMeetingSaveState("saved");
        setMeetingError("");
      } catch {
        setMeetingSaveState("error");
        setMeetingError("Saved in this browser. We will retry when connected.");
      }
    }, 800);

    return () => window.clearTimeout(timer);
  }, [meetingDraft, meetingSaveState]);

  useEffect(() => {
    if (!meetingOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMeetingOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [meetingOpen]);

  const beginBackgroundAnalysis = (image: string): Promise<SearchOutcome> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;
    setBackgroundStatus("loading");

    const pending = searchGarment([image], controller.signal)
      .then((response): SearchOutcome => {
        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return { ok: false, error: "Request cancelled", aborted: true };
        }
        normalizeResult(response);
        setBackgroundStatus("ready");
        return { ok: true, response };
      })
      .catch((caught): SearchOutcome => {
        const aborted =
          controller.signal.aborted ||
          (caught instanceof DOMException && caught.name === "AbortError");
        if (!aborted && requestId === requestIdRef.current) {
          setBackgroundStatus("error");
        }
        return {
          ok: false,
          aborted,
          error:
            caught instanceof Error
              ? caught.message
              : "Cikada could not analyse this garment. Please try again.",
        };
      });

    backgroundPromiseRef.current = pending;
    return pending;
  };

  const chooseFile = () => {
    inputRef.current?.click();
  };

  const takePhoto = () => {
    cameraInputRef.current?.click();
  };

  const onFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    abortRef.current?.abort();
    requestIdRef.current += 1;
    backgroundPromiseRef.current = null;
    setBackgroundStatus("idle");

    try {
      setError("");
      const prepared = await prepareImage(file);
      // Hold the decoded JPEG, not the File: retries reuse these bytes instead
      // of decoding the original again.
      imageRef.current = prepared.base64;
      setPreview(prepared.preview);
      setRawResult(null);
      setStatus("selected");
      void beginBackgroundAnalysis(prepared.base64);
    } catch (caught) {
      setStatus("error");
      setError(
        caught instanceof Error
          ? caught.message
          : "The selected image could not be prepared.",
      );
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    imageRef.current = null;
    setPreview(null);
    setRawResult(null);
    setError("");
    setBackgroundStatus("idle");
    backgroundPromiseRef.current = null;
    requestIdRef.current += 1;
    setStatus("idle");
  };

  const startCategorization = async () => {
    const image = imageRef.current;
    if (!image) return;

    setStatus("revealing");
    setError("");
    const minimumReveal = wait(1000);
    let outcome = await (
      backgroundPromiseRef.current ?? beginBackgroundAnalysis(image)
    );

    if (!outcome.ok && !outcome.aborted && imageRef.current === image) {
      outcome = await beginBackgroundAnalysis(image);
    }

    await minimumReveal;
    if (imageRef.current !== image || (!outcome.ok && outcome.aborted)) return;

    if (outcome.ok) {
      setRawResult(outcome.response);
      setStatus("success");
      return;
    }

    setError(outcome.error);
    setStatus("error");
  };

  const retry = () => {
    if (!imageRef.current) {
      chooseFile();
      return;
    }
    backgroundPromiseRef.current = beginBackgroundAnalysis(imageRef.current);
    void startCategorization();
  };

  const updateMeetingField = (
    field: keyof MeetingDraftInput,
    value: string,
  ) => {
    setMeetingDraft((current) => ({ ...current, [field]: value }));
    if (meetingSaveState === "sent") {
      setMeetingSaveState("idle");
    }
  };

  const flushMeetingDraft = () => {
    if (hasMeetingDetails(meetingDraft) && meetingSaveState !== "sent") {
      void saveMeetingDraft(meetingDraft, false).catch(() => undefined);
    }
  };

  const closeMeetingPanel = () => {
    flushMeetingDraft();
    setMeetingOpen(false);
  };

  const submitMeeting = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMeetingError("");

    if (!meetingDraft.name.trim() || !meetingDraft.email.includes("@")) {
      setMeetingSaveState("error");
      setMeetingError("Add your name and a valid email so we can reply.");
      return;
    }

    setMeetingSaveState("saving");
    try {
      await saveMeetingDraft(meetingDraft, true);
      lastSavedDraftRef.current = JSON.stringify(meetingDraft);
      setMeetingSaveState("sent");
    } catch {
      setMeetingSaveState("error");
      setMeetingError("We could not send this yet. Your details remain saved.");
    }
  };

  return (
    <div className={`cikada-app cikada-app--${status}`}>
      <div className="ambient ambient--one" aria-hidden="true" />
      <div className="ambient ambient--two" aria-hidden="true" />

      <header className="brand-bar">
        <img
          className="brand-logo"
          src={`${import.meta.env.BASE_URL}brand/cikada-logotype-white.png`}
          alt="Cikada"
        />
        <p className="eyebrow live-demo-badge">
          Live Demo - Children Clothes
        </p>
      </header>

      <main className="demo-main">
        <section
          className={`classifier classifier--${status}`}
          aria-label="Garment classifier"
          aria-live="polite"
        >
          <input
            ref={inputRef}
            className="visually-hidden"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={onFileSelected}
          />
          {/* Separate input: `capture` on the picker above would replace
              browsing with the camera on mobile rather than adding to it. */}
          <input
            ref={cameraInputRef}
            className="visually-hidden"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFileSelected}
          />

          {status === "idle" && (
            <div className="classifier-idle">
              <div className="classifier-idle-actions">
                <button
                  className="ck-button ck-button--primary choose-button"
                  type="button"
                  onClick={takePhoto}
                >
                  <Camera aria-hidden="true" size={19} strokeWidth={1.8} />
                  Take Photo
                </button>
                <button
                  className="ck-button ck-button--outline choose-button"
                  type="button"
                  onClick={chooseFile}
                >
                  <ImageIcon aria-hidden="true" size={19} strokeWidth={1.8} />
                  Choose Photo
                </button>
              </div>
              <p>One garment. JPEG, PNG, or WebP up to 12 MB.</p>
            </div>
          )}

          {status === "selected" && preview && (
            <div className="classifier-ready" data-analysis={backgroundStatus}>
              <div className="ready-preview-wrap">
                <img
                  className="ready-preview"
                  src={preview}
                  alt="Selected garment"
                />
                <span className="ready-count">1 photo selected</span>
              </div>
              <div className="ready-content">
                <p className="eyebrow eyebrow--outline">Photo ready</p>
                <h2>See what Cikada finds.</h2>
                <p>One click reveals the garment intelligence.</p>
                <div className="ready-actions">
                  <button
                    className="ck-button ck-button--outline"
                    type="button"
                    onClick={chooseFile}
                  >
                    Choose Another Photo
                  </button>
                  <button
                    className="ck-button ck-button--primary categorize-button"
                    type="button"
                    onClick={() => void startCategorization()}
                    aria-label={
                      backgroundStatus === "ready"
                        ? "Categorize garment, analysis ready"
                        : "Categorize garment"
                    }
                  >
                    Categorize
                    <ArrowUpRight
                      aria-hidden="true"
                      size={17}
                      strokeWidth={1.8}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}

          {status === "revealing" && (
            <div className="analysis-loading">
              {preview && (
                <div className="loading-preview-wrap">
                  <img className="loading-preview" src={preview} alt="" />
                  <span className="scan-line" aria-hidden="true" />
                </div>
              )}
              <div className="loading-copy">
                <span className="spinner" aria-hidden="true" />
                <p className="eyebrow eyebrow--outline">
                  Categorizing garment
                </p>
                <h2>Finding the signal in the fabric.</h2>
                <p>
                  Cikada is reading the visual signals in the garment.
                </p>
              </div>
            </div>
          )}

          {status === "success" && result && (
            <div className="analysis-result">
              <div className="result-image-wrap">
                {preview && (
                  <img
                    className="result-image"
                    src={preview}
                    alt="Uploaded garment"
                  />
                )}
                <span className="result-stamp">Analysis complete</span>
              </div>

              <div className="result-content">
                <div className="result-heading">
                  <div>
                    <p className="eyebrow eyebrow--outline">What Cikada sees</p>
                    <h2>Garment intelligence</h2>
                  </div>
                </div>

                {result.metrics.length > 0 && (
                  <div className="metrics" aria-label="Resale potential">
                    {result.metrics.map((metric) => (
                      <div className="metric" key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                      </div>
                    ))}
                  </div>
                )}

                {result.attributes.length > 0 && (
                  <div className="attributes">
                    {result.attributes.map((attribute) => (
                      <div className="attribute" key={attribute.key}>
                        <span>{attribute.label}</span>
                        <strong>{attribute.value}</strong>
                        {attribute.confidence && (
                          <small>{attribute.confidence}</small>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="result-actions">
                  <button
                    className="ck-button ck-button--outline"
                    type="button"
                    onClick={reset}
                  >
                    <RefreshCw aria-hidden="true" size={17} strokeWidth={1.8} />
                    Choose Another Photo
                  </button>
                </div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="analysis-error">
              {preview && <img className="error-preview" src={preview} alt="" />}
              <div>
                <p className="eyebrow eyebrow--outline">Analysis interrupted</p>
                <h2>We could not read this garment.</h2>
                <p role="alert">{error}</p>
                <div className="error-actions">
                  <button
                    className="ck-button ck-button--primary"
                    type="button"
                    onClick={retry}
                  >
                    Try Again
                  </button>
                  <button
                    className="ck-button ck-button--outline"
                    type="button"
                    onClick={chooseFile}
                  >
                    Choose Another Photo
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        <aside className="meeting-cta" aria-labelledby="meeting-heading">
          <div>
            <p className="eyebrow eyebrow--outline">Make it operational</p>
            <h2 id="meeting-heading">Meet tomorrow?</h2>
            <p>
              Talk with us about textile intelligence, robotics, investment,
              or joining the team.
            </p>
          </div>
          <button
            className="ck-button ck-button--primary"
            type="button"
            onClick={() => setMeetingOpen(true)}
          >
            <CalendarDays aria-hidden="true" size={17} strokeWidth={1.8} />
            Meet tomorrow?
            <ArrowUpRight aria-hidden="true" size={17} strokeWidth={1.8} />
          </button>
        </aside>
      </main>

      {meetingOpen && (
        <div
          className="meeting-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeMeetingPanel();
            }
          }}
        >
          <section
            className="meeting-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="meeting-panel-heading"
          >
            <div className="meeting-panel-header">
              <div>
                <p className="eyebrow eyebrow--outline">Meet tomorrow?</p>
                <h2 id="meeting-panel-heading">Start the conversation.</h2>
              </div>
              <button
                className="panel-close"
                type="button"
                aria-label="Close meeting form"
                onClick={closeMeetingPanel}
              >
                <X aria-hidden="true" size={22} strokeWidth={1.8} />
              </button>
            </div>

            {meetingSaveState === "sent" ? (
              <div className="meeting-confirmation" aria-live="polite">
                <p className="eyebrow">Request received</p>
                <h3>Tomorrow sounds good.</h3>
                <p>
                  We have your details and will follow up at {meetingDraft.email}.
                </p>
                <button
                  className="ck-button ck-button--outline"
                  type="button"
                  onClick={closeMeetingPanel}
                >
                  Close
                </button>
              </div>
            ) : (
              <form className="meeting-form" onSubmit={submitMeeting}>
                <p className="draft-notice">
                  Details are saved as you type so our team can follow up even
                  if you close this panel.
                </p>

                <label>
                  <span>Name</span>
                  <input
                    name="name"
                    autoComplete="name"
                    value={meetingDraft.name}
                    onChange={(event) =>
                      updateMeetingField("name", event.target.value)
                    }
                    onBlur={flushMeetingDraft}
                    placeholder="Your name"
                    required
                  />
                </label>

                <label>
                  <span>Work email</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={meetingDraft.email}
                    onChange={(event) =>
                      updateMeetingField("email", event.target.value)
                    }
                    onBlur={flushMeetingDraft}
                    placeholder="you@company.com"
                    required
                  />
                </label>

                <label>
                  <span>Company</span>
                  <input
                    name="organization"
                    autoComplete="organization"
                    value={meetingDraft.organization}
                    onChange={(event) =>
                      updateMeetingField("organization", event.target.value)
                    }
                    onBlur={flushMeetingDraft}
                    placeholder="Company or organisation"
                  />
                </label>

                <label>
                  <span>I want to talk about</span>
                  <select
                    name="intent"
                    value={meetingDraft.intent}
                    onChange={(event) =>
                      updateMeetingField("intent", event.target.value)
                    }
                    onBlur={flushMeetingDraft}
                  >
                    <option value="customer">Using Cikada</option>
                    <option value="investor">Investing in Cikada</option>
                    <option value="talent">Joining Cikada</option>
                  </select>
                </label>

                <label>
                  <span>Anything useful to know?</span>
                  <textarea
                    name="message"
                    rows={4}
                    value={meetingDraft.message}
                    onChange={(event) =>
                      updateMeetingField("message", event.target.value)
                    }
                    onBlur={flushMeetingDraft}
                    placeholder="A few words are enough"
                  />
                </label>

                <div className="meeting-form-footer">
                  <span className={`save-state save-state--${meetingSaveState}`}>
                    {meetingSaveState === "saving"
                      ? "Saving..."
                      : meetingSaveState === "saved"
                        ? "Draft saved"
                        : meetingError}
                  </span>
                  <button
                    className="ck-button ck-button--primary"
                    type="submit"
                  >
                    Request a Meeting
                    <ArrowUpRight
                      aria-hidden="true"
                      size={17}
                      strokeWidth={1.8}
                    />
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default App;
